/**
 *  @class
 *  @function CollectionTabs
 */

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this._onBlockSelect = null;
      this._sectionEl = null;
      this._ready = false;
      this._pendingBlock = null;
      this._fetchCache = new Map();
      this._fetchPromises = new Map();
    }

    connectedCallback() {
      this._sectionEl = this.closest('.shopify-section');

      // Attach the block-select listener synchronously (before the rAF setup
      // below) so a re-render that re-fires shopify:block:select can't slip
      // through before we are listening. Listen on the section element: the
      // event bubbles there, and the promotion cards that share a block id
      // live outside <collection-tabs>.
      if (window.Shopify && Shopify.designMode) {
        this._onBlockSelect = (event) => this.selectTabForBlock(event.target);
        (this._sectionEl || this).addEventListener('shopify:block:select', this._onBlockSelect);
      }

      requestAnimationFrame(() => {
        this.buttons = Array.from(this.querySelectorAll('button'));
        this.sectionHeader = this.closest('.section-header');
        if (!this.sectionHeader) return;
        this.links = Array.from(this.sectionHeader.querySelectorAll('.linked-to-tab'));
        this.target = this.dataset.target;
        this.slider = document.getElementById(this.target);
        this.themeScroll = this.sectionHeader.nextElementSibling;

        if (!this.slider) return;

        this.productLimit = parseInt(this.slider.dataset.productLimit, 10) || 6;

        this.collectionMap = new Map();
        this.allItems = this.slider.querySelectorAll('[data-collection]');
        this.allItems.forEach((item) => {
          let handle = item.dataset.collection;
          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }
          this.collectionMap.get(handle).push(item);
        });

        this.promotionMap = new Map();
        if (this._sectionEl) {
          this._sectionEl.querySelectorAll('.collection-tabs--promotions [data-collection]').forEach((promotion) => {
            this.promotionMap.set(promotion.dataset.collection, promotion);
          });
        }

        this.addEventListener('click', (event) => {
          let button = event.target.closest('button');
          if (!button) return;
          event.preventDefault();
          this.activateTab(button);
        });

        this._ready = true;
        this.prefetchInactiveTabs();

        // Apply a block selection that arrived before setup completed.
        if (this._pendingBlock) {
          this.selectTabForBlock(this._pendingBlock);
          this._pendingBlock = null;
        }
      });
    }

    disconnectedCallback() {
      if (this._onBlockSelect) {
        (this._sectionEl || this).removeEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    selectTabForBlock(target) {
      if (!this._ready) {
        this._pendingBlock = target;
        return;
      }
      if (!target) return;
      let button = this.buttons.find((item) => item === target);
      let handle = target.dataset ? target.dataset.collection : null;
      if (!button && handle) {
        button = this.buttons.find((item) => item.dataset.collection === handle);
      }
      if (button) {
        this.activateTab(button);
      }
    }

    activateTab(button) {
      let index = this.buttons.indexOf(button);
      if (index === -1) return;
      this.toggleButtons(index);
      this.toggleLinks(index);
      if (button.dataset.collection) {
        this.handleTabClick(button);
      }
    }

    toggleButtons(index) {
      this.buttons.forEach((button) => button.classList.remove('active'));
      this.buttons[index].classList.add('active');
    }

    toggleLinks(index) {
      if (this.links.length && this.links[index]) {
        this.links.forEach((link) => link.classList.remove('active'));
        this.links[index].classList.add('active');
      }
    }

    toggleCollection(handle) {
      if (!this.slider) return;

      this.allItems.forEach((item) => item.classList.remove('carousel__slide'));
      let activeItems = this.collectionMap.get(handle);
      if (activeItems) {
        activeItems.forEach((item) => item.classList.add('carousel__slide'));
      }

      if (!this.themeScroll) return;
      this.themeScroll.init();
      let inner = this.themeScroll.querySelector('.theme-scroll--inner');
      if (inner) inner.scroll({ top: 0, left: 0 });
    }

    handleTabClick(button) {
      let handle = button.dataset.collection;
      if (this.collectionMap.has(handle)) {
        this.toggleCollection(handle);
        return;
      }
      let url = button.dataset.collectionUrl;
      if (!url) {
        this.toggleCollection(handle);
        return;
      }
      this.fetchCollection(handle, url).then(() => {
        this.toggleCollection(handle);
      });
    }

    prefetchInactiveTabs() {
      let loadTabs = () => {
        this.buttons.forEach((button, index) => {
          if (index === 0) return;
          let handle = button.dataset.collection;
          let url = button.dataset.collectionUrl;
          if (!handle || !url || this.collectionMap.has(handle)) return;
          this.fetchCollection(handle, url);
        });
      };

      if (document.readyState === 'complete') {
        loadTabs();
      } else {
        window.addEventListener('load', loadTabs, { once: true });
      }
    }

    fetchCollection(handle, collectionUrl) {
      if (this._fetchCache.has(handle)) {
        return Promise.resolve();
      }
      if (this._fetchPromises.has(handle)) {
        return this._fetchPromises.get(handle);
      }

      let fetchUrl = collectionUrl + '?section_id=collection-tabs-ajax';
      let promise = fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          let parser = new DOMParser();
          let doc = parser.parseFromString(html, 'text/html');
          let allNodes = Array.from(doc.querySelectorAll('[data-collection]'));
          let viewAllNode = allNodes.find((node) => node.classList.contains('collection-tabs--view-all'));
          let productNodes = allNodes.filter((node) => !node.classList.contains('collection-tabs--view-all'));
          let items = productNodes.slice(0, this.productLimit);

          if (viewAllNode) {
            let totalCount = parseInt(viewAllNode.dataset.productCount, 10) || productNodes.length;
            if (totalCount > this.productLimit) {
              items.push(viewAllNode);
            }
          }

          let promotion = this.promotionMap.get(handle);
          if (promotion) {
            let position = parseInt(promotion.dataset.promotionPosition, 10) || 1;
            if (position <= items.length) {
              items.splice(position - 1, 0, promotion);
            }
          }

          items.forEach((item) => {
            this.slider.appendChild(item);
          });

          this.slider.querySelectorAll('.lazyload').forEach((img) => {
            lazySizes.loader.unveil(img);
          });

          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }
          items.forEach((item) => {
            this.collectionMap.get(handle).push(item);
          });
          this.allItems = this.slider.querySelectorAll('[data-collection]');
          this._fetchCache.set(handle, true);
          this._fetchPromises.delete(handle);
        })
        .catch(() => {
          this._fetchPromises.delete(handle);
        });

      this._fetchPromises.set(handle, promise);
      return promise;
    }
  }
  customElements.define('collection-tabs', CollectionTabs);
}
